import React, { useMemo, useState } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Box,
    Typography,
    TablePagination,
    TableSortLabel,
} from "@mui/material";
import Styles from "./IssueTypeDrillDownTable.module.scss";

// Comparator functions
function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
}

const IssueTypeDrillDownTable = ({ data, handleView }) => {
    // Helpers
    const timeToMinutes = (timeStr) => {
        if (!timeStr || typeof timeStr !== "string" || !timeStr.includes(":")) return 0;
        const [h, m] = timeStr.split(":").map(Number);
        return (h || 0) * 60 + (m || 0);
    };

    const minutesToTime = (mins) => {
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
    };

    // State for Pagination and Sorting
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("userName");

    // Handle Sort
    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    // Handle Pagination
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // 1. Process Data into FLAT list of Task Items (with necessary properties for sort/display)
    const flattenedTasks = useMemo(() => {
        if (!data || data.length === 0) return [];

        const tasks = [];
        // User Totals Cache - Calculate total per user per issue type
        const userTotalMap = {};

        // Pre-calculate user totals per issue type
        data.forEach(item => {
            const pName = item.projectName || "Unknown Project";
            const uName = item.userName || "Unknown User";
            const iType = item.issueType || "N/A";
            const key = uName + pName + iType; // Include issue type for per-section totals
            userTotalMap[key] = (userTotalMap[key] || 0) + timeToMinutes(item.totalHours);
        });

        data.forEach(item => {
            const duration = timeToMinutes(item.totalHours);
            const pName = item.projectName || "Unknown Project";
            const uName = item.userName || "Unknown User";
            const iType = item.issueType || "N/A";
            const key = uName + pName + iType;

            tasks.push({
                projectName: pName,
                userName: uName,
                issueType: iType,
                taskNo: item.taskNo,
                title: item.title || `Task ${item.taskNo}`,
                taskId: item._id,
                workingHours: item.workingHours,
                workingHoursValue: duration, // for sorting tasks
                userTotalMinutes: userTotalMap[key], // Total for this user in this issue type
                formattedUserTotal: minutesToTime(userTotalMap[key]),
                original: item
            });
        });
        return tasks;
    }, [data]);

    // 2. Sort the Flattened List
    const sortedTasks = useMemo(() => {
        // Custom Comparator Logic
        const comparator = (a, b) => {
            // 0. Group by Issue Type FIRST (for Total Hours view)
            const issueTypeDiff = a.issueType.localeCompare(b.issueType);
            if (issueTypeDiff !== 0) return issueTypeDiff;

            // 1. Primary Sort: Always by Project Name to maintain high-level Project buckets
            const projectDiff = a.projectName.localeCompare(b.projectName);
            if (projectDiff !== 0) return projectDiff;

            const multiplier = order === 'asc' ? 1 : -1;

            // 2. Specific Columns
            switch (orderBy) {
                case 'userName':
                    return a.userName.localeCompare(b.userName) * multiplier;

                case 'userTotalMinutes':
                    // Sort by User Total Time
                    if (a.userTotalMinutes !== b.userTotalMinutes) {
                        return (a.userTotalMinutes - b.userTotalMinutes) * multiplier;
                    }
                    return a.userName.localeCompare(b.userName); // Fallback to name

                case 'title':
                    // Sort by Title (Tasks) - Breaks User Grouping if needed
                    return a.title.localeCompare(b.title) * multiplier;

                case 'workingHoursValue':
                    // Sort by Task Duration - Breaks User Grouping if needed
                    return (a.workingHoursValue - b.workingHoursValue) * multiplier;

                default:
                    // Default fallback
                    return a.userName.localeCompare(b.userName) * multiplier;
            }
        };

        return flattenedTasks.sort(comparator);
    }, [flattenedTasks, order, orderBy]);


    // 3. Paginate
    const visibleTasks = useMemo(() => {
        return sortedTasks.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [sortedTasks, page, rowsPerPage]);


    // 4. Calculate RowSpans for the VISIBLE SLICE and add Issue Type Headers
    const processedPageRows = useMemo(() => {
        const rows = [];
        let currentIssueType = null;

        // We need to look ahead in 'visibleTasks' to calculate spans
        let i = 0;
        while (i < visibleTasks.length) {
            const current = visibleTasks[i];

            // Add Issue Type Header Row if issue type changes
            if (current.issueType !== currentIssueType) {
                currentIssueType = current.issueType;
                // Count how many tasks in this issue type
                let issueTypeCount = 0;
                for (let c = i; c < visibleTasks.length; c++) {
                    if (visibleTasks[c].issueType === currentIssueType) {
                        issueTypeCount++;
                    } else {
                        break;
                    }
                }
                rows.push({
                    isIssueTypeHeader: true,
                    issueType: currentIssueType,
                    issueTypeCount: issueTypeCount
                });
            }

            const currentProject = current.projectName;
            const currentUser = current.userName;

            // Calculate User Span within this Page
            let userSpan = 1;
            for (let j = i + 1; j < visibleTasks.length; j++) {
                if (visibleTasks[j].userName === currentUser && visibleTasks[j].projectName === currentProject && visibleTasks[j].issueType === currentIssueType) {
                    userSpan++;
                } else {
                    break;
                }
            }

            // Calculate Project Span (if needed, but usually we just span user)
            // Since project column merges everything, we need project span too.
            let projectSpan = 1;
            // If this is the FIRST row of the project on this page, calculate span
            // But actually, we just need to know if we should render the project cell.
            // Simplified: Render Project Cell for first row of the page, or if project changes.
            // RowSpan = count of remaining rows with same project.
            for (let k = i + 1; k < visibleTasks.length; k++) {
                if (visibleTasks[k].projectName === currentProject && visibleTasks[k].issueType === currentIssueType) {
                    projectSpan++;
                } else {
                    break;
                }
            }

            // Determine if we should render grouping cells
            // Check if previous row (in this page) was same structure? 
            // No, we iterate fully.

            // We push 'userSpan' rows.
            for (let u = 0; u < userSpan; u++) {
                const task = visibleTasks[i + u];
                const isFirstOfProjectFn = (i + u === 0) || (visibleTasks[i + u - 1].projectName !== task.projectName) || (visibleTasks[i + u - 1].issueType !== task.issueType);

                // Calculate effective project span from this specific row
                let effectiveProjectSpan = 0;
                if (isFirstOfProjectFn) {
                    let pCount = 0;
                    for (let r = i + u; r < visibleTasks.length; r++) {
                        if (visibleTasks[r].projectName === task.projectName && visibleTasks[r].issueType === task.issueType) pCount++;
                        else break;
                    }
                    effectiveProjectSpan = pCount;
                }

                rows.push({
                    ...task,
                    projectRowSpan: isFirstOfProjectFn ? effectiveProjectSpan : 0,
                    userRowSpan: u === 0 ? userSpan : 0,
                });
            }

            i += userSpan;
        }
        return rows;
    }, [visibleTasks]);


    // Headers Config
    const headCells = [
        { id: 'projectName', label: 'Project Name', align: 'center', sortable: false }, // Project usually static
        { id: 'userName', label: 'Name', align: 'center', sortable: true },
        { id: 'title', label: 'Title', align: 'center', sortable: true },
        { id: 'workingHoursValue', label: 'Work Log Time', align: 'center', sortable: true }, // Changed sortable to true
        { id: 'formattedUserTotal', label: 'Total Working', align: 'center', sortable: true, idForSort: 'userTotalMinutes' },
    ];

    const createSortHandler = (property) => (event) => {
        handleRequestSort(property);
    };

    return (
        <Box>
            <div className={Styles.renderingTable}>
                <TableContainer className={Styles.tableContainer}>
                    <Box className={Styles.tableContent}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {headCells.map((headCell) => (
                                        <TableCell
                                            key={headCell.id}
                                            className={`${Styles.TableCell} ${headCell.id === 'projectName' ? Styles.ProjectCell : ''}`}
                                            // Only apply ProjectCell border logic if it was body, but here it's header. Styles.ProjectCell has NO border now in combined selector. Good.
                                            sortDirection={orderBy === (headCell.idForSort || headCell.id) ? order : false}
                                        >
                                            {headCell.sortable ? (
                                                <TableSortLabel
                                                    active={orderBy === (headCell.idForSort || headCell.id)}
                                                    direction={orderBy === (headCell.idForSort || headCell.id) ? order : 'asc'}
                                                    onClick={createSortHandler(headCell.idForSort || headCell.id)}
                                                >
                                                    {headCell.label}
                                                    {orderBy === (headCell.idForSort || headCell.id) ? (
                                                        <Box component="span" sx={{ ...Styles.visuallyHidden, display: 'none' }}>
                                                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                                        </Box>
                                                    ) : null}
                                                </TableSortLabel>
                                            ) : (
                                                headCell.label
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {processedPageRows.length > 0 ? (
                                    processedPageRows.map((row, index) => {
                                        // Render Issue Type Header Row
                                        if (row.isIssueTypeHeader) {
                                            return (
                                                <TableRow key={`issue-type-${index}`}>
                                                    <TableCell
                                                        colSpan={5}
                                                        sx={{
                                                            background: 'linear-gradient(135deg, #F3F4F6 0%, #E5E7EB 100%)',
                                                            color: '#374151',
                                                            fontWeight: 700,
                                                            fontSize: '13px',
                                                            textAlign: 'left',
                                                            padding: '10px 16px',
                                                            borderBottom: '2px solid #D1D5DB',
                                                            borderTop: '2px solid #D1D5DB'
                                                        }}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Box
                                                                sx={{
                                                                    width: 4,
                                                                    height: 16,
                                                                    bgcolor: '#6B7280',
                                                                    borderRadius: '2px'
                                                                }}
                                                            />
                                                            {row.issueType} <span style={{ color: '#6B7280', fontWeight: 600 }}>({row.issueTypeCount} {row.issueTypeCount === 1 ? 'task' : 'tasks'})</span>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        }

                                        // Render Normal Task Row
                                        return (
                                            <TableRow
                                                key={index}
                                                hover
                                                sx={{ "&:hover": { backgroundColor: "#f0f8ff !important" } }}
                                            >
                                                {/* Project Name */}
                                                {row.projectRowSpan > 0 && (
                                                    <TableCell
                                                        className={`${Styles.TableBodyCell} ${Styles.SpannedCell} ${Styles.ProjectCell}`}
                                                        rowSpan={row.projectRowSpan}
                                                        sx={{ fontWeight: "700 !important" }}
                                                    >
                                                        {row.projectName}
                                                    </TableCell>
                                                )}

                                                {/* User Name */}
                                                {row.userRowSpan > 0 && (
                                                    <TableCell
                                                        className={`${Styles.TableBodyCell} ${Styles.SpannedCell} ${Styles.NameCell}`}
                                                        rowSpan={row.userRowSpan}
                                                    >
                                                        {row.userName}
                                                    </TableCell>
                                                )}

                                                {/* Title */}
                                                <TableCell
                                                    className={Styles.TableBodyCell}
                                                    onClick={() => handleView && handleView({ _id: row.taskId })}
                                                    sx={{
                                                        maxWidth: "200px",
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                        whiteSpace: "nowrap",
                                                        cursor: "pointer",
                                                        color: "#1976d2",
                                                        "&:hover": { textDecoration: "underline" }
                                                    }}
                                                    title={row.title}
                                                >
                                                    {row.title}
                                                </TableCell>

                                                {/* Work Log Time */}
                                                <TableCell className={Styles.TableBodyCell}>
                                                    {row.workingHours}
                                                </TableCell>

                                                {/* User Total */}
                                                {row.userRowSpan > 0 && (
                                                    <TableCell
                                                        className={`${Styles.TableBodyCell} ${Styles.SpannedCell} ${Styles.TotalCell}`}
                                                        rowSpan={row.userRowSpan}
                                                        sx={{ fontWeight: "bold !important", color: "#008080 !important" }}
                                                    >
                                                        {row.formattedUserTotal}
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ padding: "20px" }}>
                                            <Typography color="textSecondary">No Data Available</Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Box>
                    <TablePagination
                        rowsPerPageOptions={[10, 25, 100]}
                        component="div"
                        count={data ? data.length : 0}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </TableContainer>
            </div>
        </Box>
    );
};

export default IssueTypeDrillDownTable;
